/**
 * M2 (Ely 10-31): the one table of where a listing sells. A listing's marketplace id (catalog.listing_content
 * marketplace_id, on the wire as channels[].markets[].marketplace_id) -> ISO 3166-1 alpha-2 country -> region, for every
 * channel: Amazon SP marketplace ids, eBay site ids (EBAY_US), and channels that send the country itself (Walmart `US`).
 * '' (Shopify and other stores) and unknown ids have no country. One Amazon connection can sell in several countries
 * (Amazon North America: US, CA, MX, BR), so the country is per listing marketplace, never per connection.
 */

/** Amazon SP-API marketplace ids. */
const AMAZON_MARKETPLACES: Record<string, string> = {
  ATVPDKIKX0DER: 'US',
  A2EUQ1WTGCTBG2: 'CA',
  A1AM78C64UM0Y8: 'MX',
  A2Q3Y263D00KWC: 'BR',
  A1F83G8C2ARO7P: 'GB',
  A1PA6795UKMFR9: 'DE',
  A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT',
  A1RKKUPIHCS9HS: 'ES',
  A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
  A1C3SOZRARQ6R3: 'PL',
  A33AVAJ2PDY3EV: 'TR',
  AMEN7PMS3EDWL: 'BE',
  A28R8C7NBKEWEA: 'IE',
  A2VIGQ35RCS4UG: 'AE',
  A17E79C6D8DWNP: 'SA',
  ARBP9OOSHTCHU: 'EG',
  AE08WJ6YKNBMC: 'ZA',
  A21TJRUUN4KGV: 'IN',
  A1VC38T7YXB528: 'JP',
  A39IBJ37TRP1C6: 'AU',
  A19VAU5U5O7RUS: 'SG',
};

/** eBay site ids (connections-adapters ebay/config.ts EBAY_SITES); EBAY_GB is the UK. */
const EBAY_MARKETPLACES: Record<string, string> = {
  EBAY_US: 'US',
  EBAY_CA: 'CA',
  EBAY_GB: 'GB',
  EBAY_AU: 'AU',
  EBAY_DE: 'DE',
  EBAY_IT: 'IT',
};

/** Regions in the order the picker lists them, each with its countries in order. */
export const MARKET_REGIONS: ReadonlyArray<{
  name: string;
  countries: readonly string[];
}> = [
  { name: 'North America', countries: ['US', 'CA', 'MX'] },
  { name: 'South America', countries: ['BR'] },
  {
    name: 'Europe',
    countries: [
      'GB',
      'DE',
      'FR',
      'IT',
      'ES',
      'NL',
      'SE',
      'PL',
      'BE',
      'IE',
      'TR',
    ],
  },
  { name: 'Middle East & Africa', countries: ['AE', 'SA', 'EG', 'ZA'] },
  { name: 'Asia Pacific', countries: ['JP', 'AU', 'SG', 'IN'] },
];

/** The filter token of listings with no country (marketplace id ''): stores such as Shopify. */
export const NO_COUNTRY = 'none';

/**
 * The countries a selection reaches (Catalog Division Scope 6): picking a country keeps the no-country stores, since
 * they have no country to exclude them by. Only picking nothing but NO_COUNTRY narrows to them; empty stays empty.
 */
export const withNoCountry = (countries: readonly string[]): string[] =>
  countries.length && !countries.includes(NO_COUNTRY)
    ? [...countries, NO_COUNTRY]
    : [...countries];

const KNOWN_COUNTRIES = new Set(MARKET_REGIONS.flatMap(r => r.countries));

/** The country a marketplace id sells in, or null ('' and unknown ids). */
export const countryOfMarketplace = (
  marketplaceId: string | null | undefined,
): string | null => {
  if (!marketplaceId) return null;
  const mapped =
    AMAZON_MARKETPLACES[marketplaceId] ?? EBAY_MARKETPLACES[marketplaceId];
  if (mapped) return mapped;
  const iso = marketplaceId.toUpperCase();
  return KNOWN_COUNTRIES.has(iso) ? iso : null;
};

/** The region a country is in ("North America"), or null for a country the table does not list. */
export const regionOfCountry = (country: string): string | null =>
  MARKET_REGIONS.find(r => r.countries.includes(country))?.name ?? null;

const COUNTRY_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

/** "United States", "Mexico"; the code itself when the runtime has no name. */
export const countryName = (country: string): string =>
  COUNTRY_NAMES.of(country) ?? country;

/** The country's flag as regional-indicator emoji ("🇺🇸"); no image assets. */
export const countryFlag = (country: string): string =>
  /^[A-Z]{2}$/.test(country)
    ? String.fromCodePoint(
        ...[...country].map(c => 0x1f1e6 + c.charCodeAt(0) - 65),
      )
    : '';

/**
 * Every marketplace id that sells in these countries, for the list's `markets` filter: the Amazon and eBay ids and the
 * code itself (channels that send the country). NO_COUNTRY stays as the token for marketplace id ''.
 */
export const marketplaceIdsOf = (countries: readonly string[]): string[] => {
  const wanted = new Set(countries);
  const ids: string[] = [];
  for (const table of [AMAZON_MARKETPLACES, EBAY_MARKETPLACES]) {
    for (const [id, country] of Object.entries(table)) {
      if (wanted.has(country)) ids.push(id);
    }
  }
  for (const country of countries) ids.push(country);
  return ids;
};
